import type { WalletStatusSectionProps } from "@/features/Wallet/WalletStatusSection.vue";
import type { BaseMedallionProps } from "@/library/BaseMedallion.vue";
import { EDDBehaviorEnum, type KYCApiResponse } from "@/types/Wallet/kyc";

export type KYCStatusButtonAction =
  | "backToDashboard"
  | "tryAgain"
  | "emailSupport";

export interface KYCStatusSpecs {
  state:
    | "LOADING"
    | "PENDING"
    | "MAX_ATTEMPTS_REACHED"
    | "MANUAL_REVIEW"
    | "RETRY_KYC";
  statusSectionProps: WalletStatusSectionProps;
  primaryButtonAction?: KYCStatusButtonAction;
  secondaryButtonAction?: KYCStatusButtonAction;
}

// EDD Document Type to readable text mapping
export const EDD_DOCUMENT_EXAMPLES: Record<string, string[]> = {
  GOVERNMENT_ISSUED_ID: [
    "U.S. Driver's License",
    "U.S. Passport or Passport Card",
  ],
  SSN_PROOF: ["Social Security Card", "W-2 Form"],
  ADDRESS_PROOF: [
    "Utility bill (gas, water, electric)",
    "Bank or credit card statement",
  ],
};

/**
 * Composable for handling KYC and EDD (Enhanced Due Diligence) logic
 */
export function useKYC() {
  const getEddDocumentsDescriptions = (documentTypes: string[]) => {
    if (documentTypes.length === 0) {
      return [];
    }

    return documentTypes
      .map((type) => {
        const docExamples = EDD_DOCUMENT_EXAMPLES[type];
        if (docExamples == null) {
          console.warn(`Unknown document type: ${type}`);
          return [""];
        }
        return docExamples;
      })
      .filter(Boolean);
  };

  const _getStatusesButton = () => {
    return {
      backButton: {
        text: "Back to dashboard",
        action: "backToDashboard",
      },
      tryAgainButton: {
        text: "Try again",
        action: "tryAgain",
      },
      emailSupportButton: {
        text: "Contact Cloaked Support",
        action: "emailSupport",
      },
    } as Record<
      "backButton" | "tryAgainButton" | "emailSupportButton",
      {
        text: string;
        action: KYCStatusButtonAction;
      }
    >;
  };

  const _getStatusesMedallion = () => {
    return {
      error: {
        size: "lg",
        color: "error",
        icon: "info",
      },
      pending: {
        size: "lg",
        color: "yield",
        icon: "calendar",
      },
    } as Record<"error" | "pending", BaseMedallionProps>;
  };

  const getKYCFailureMessage = (
    data: KYCApiResponse["data"]
  ): KYCStatusSpecs => {
    const { backButton, tryAgainButton, emailSupportButton } =
      _getStatusesButton();
    const { error: errorMedallion, pending: pendingMedallion } =
      _getStatusesMedallion();

    const defaultFailureStatus: KYCStatusSpecs = {
      state: "RETRY_KYC",
      statusSectionProps: {
        title: "Unable to verify your identity.",
        description:
          "Please try again or contact Cloaked Support for more assistance.",
        medallion: errorMedallion,
        primaryButtonText: tryAgainButton.text,
        secondaryButtonText: emailSupportButton.text,
      },
      primaryButtonAction: tryAgainButton.action,
      secondaryButtonAction: emailSupportButton.action,
    };

    if (!data) {
      return defaultFailureStatus;
    }

    const { message, edd } = data || {};
    const { behavior, remaining_attempts } = edd || {};

    // Pending
    if (data?.PENDING && data.PENDING.length > 0) {
      return {
        state: "PENDING",
        statusSectionProps: {
          title: "We're reviewing your information.",
          description:
            "Thanks for submitting your documentation — our team is taking a look now. We'll be in touch as soon as there's an update.",
          medallion: pendingMedallion,
          primaryButtonText: backButton.text,
          secondaryButtonText: emailSupportButton.text,
        },
        primaryButtonAction: backButton.action,
        secondaryButtonAction: emailSupportButton.action,
      };
    }

    // Pending - Manual review
    if (behavior === EDDBehaviorEnum.MANUAL_REVIEW) {
      return {
        state: "MANUAL_REVIEW",
        statusSectionProps: {
          // No other props because we're using a KycManualReviewDocsList component
        },
      };
    }

    // Error - Max attempts reached
    if (
      (message && message.includes("Max KYC attempts reached.")) ||
      remaining_attempts === 0
    ) {
      return {
        state: "MAX_ATTEMPTS_REACHED",
        statusSectionProps: {
          title: "We couldn't verify your identity at this time.",
          description:
            "Unfortunately, you're no longer eligible to try again right now. You can still return to your dashboard or reach out to our support team if you have questions.",
          medallion: errorMedallion,
          primaryButtonText: backButton.text,
          secondaryButtonText: emailSupportButton.text,
        },
        primaryButtonAction: backButton.action,
        secondaryButtonAction: emailSupportButton.action,
      };
    }

    // Error - Can retry
    if (
      remaining_attempts &&
      remaining_attempts > 0 &&
      behavior === EDDBehaviorEnum.RETRY_KYC
    ) {
      const isLastAttempt = remaining_attempts === 1;

      const title = isLastAttempt
        ? "You have one final attempt remaining."
        : "Unable to verify your identity.";

      const description = isLastAttempt
        ? "Please double-check your information before submitting. If you're unsure, feel free to reach out to support@cloaked.com for help with manual verification."
        : "Please try again or contact Cloaked Support for more assistance.";

      return {
        state: "RETRY_KYC",
        statusSectionProps: {
          title,
          description,
          medallion: errorMedallion,
          primaryButtonText: tryAgainButton.text,
          secondaryButtonText: emailSupportButton.text,
        },
        primaryButtonAction: tryAgainButton.action,
        secondaryButtonAction: emailSupportButton.action,
      };
    }

    return defaultFailureStatus;
  };

  return {
    getEddDocumentsDescriptions,
    getKYCFailureMessage,
  };
}
